import type { ErrorEvent } from '@pa/api/clinic'
import { fmtDateTimeOf, timeAgo, TruncatedText } from '@pa/ui'
import { contextLine } from '../model/format'
import { isErrorStep, STEP_CLASS, stepLabel } from '../model/steps'

export interface ErrorTableProps {
  rows: readonly ErrorEvent[]
  /** Times: the clinic's zone on C-03, the viewer's on S-08. */
  timeZone: string
  /** S-08: the Clinic column. */
  showClinic: boolean
  onOpen: (id: string) => void
}

/** A step as the mockup's coloured pill (`PF lookup` / `Availability` / `Hold` / `PF write`). */
export function StepPill({ step }: { step: string }) {
  return <span className={`step-pill ${isErrorStep(step) ? STEP_CLASS[step] : ''}`}>{stepLabel(step)}</span>
}

/**
 * The C-03 table (UserFlows C-03, mockup `ErrorsPage.tsx`), newest first as the API sorts it: Time (+ relative),
 * Step, Error type, Message (truncated, the full text on hover), Context ("Visit type · Location · Provider" or "—"),
 * Source (or "Unknown"). S-08 adds Clinic. The row click opens C-03a; the code button is the keyboard way in.
 */
export function ErrorTable({ rows, timeZone, showClinic, onOpen }: ErrorTableProps) {
  return (
    <table className="table rtable err-table">
      <thead>
        <tr>
          <th>Time</th>
          {showClinic && <th>Clinic</th>}
          <th>Step</th>
          <th>Error type</th>
          <th>Message</th>
          <th>Context</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <tr
            key={e.id}
            className="clickable"
            onClick={() => {
              onOpen(e.id)
            }}
          >
            <td className="c-time" style={{ whiteSpace: 'nowrap' }}>
              <b style={{ fontWeight: 600 }}>{fmtDateTimeOf(e.occurredAt, timeZone)}</b>
              <span className="cell-sub">{timeAgo(e.occurredAt)}</span>
            </td>
            {showClinic && (
              <td className="c-clinic" data-label="Clinic">
                {e.clinic?.name ?? '—'}
              </td>
            )}
            <td className="c-step">
              <StepPill step={e.step} />
            </td>
            <td className="c-code" data-label="Error type">
              <button
                type="button"
                className="err-code err-open"
                aria-label={`Open error ${e.code}, ${fmtDateTimeOf(e.occurredAt, timeZone)}`}
                onClick={(ev) => {
                  ev.stopPropagation()
                  onOpen(e.id)
                }}
              >
                {e.code}
              </button>
            </td>
            <td className="c-msg" data-label="Message">
              <TruncatedText text={e.message} maxWidth={280} />
            </td>
            <td className="c-ctx" data-label="Context">
              <span className="muted">{contextLine(e.context) || '—'}</span>
            </td>
            <td className="c-source" data-label="Source">
              {e.source ?? <span className="src-unknown">Unknown</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
